import React from 'react';
import { CartesianGrid, Line, LineChart, Tooltip, XAxis, YAxis } from 'recharts';
import styled from 'styled-components';

import { seriesStop, tokens } from 'containers/Intelligence/theme';
import { formatNumber } from 'utils/formatters';

import useMeasuredWidth from '../useMeasuredWidth';

export interface IMovementTrendChartProps {
  /** Readings in chronological order; a non-finite reading breaks the line instead of plotting 0. */
  points: number[];
  /** Tick captions, index-aligned with the readings. */
  pointLabels?: string[];
  /**
   * The y-axis ticks, drawn exactly as given and in the order given. They also fix the domain, so a
   * movement series that swings either side of zero keeps the frame's own symmetric scale instead of
   * being re-based on its own extremes. PLA-8828.
   */
  axisTicks: number[];
  /** A `tokens.color` value — the stroke of the trajectory. */
  color?: string;
  /** A `tokens.color` value for the vertices; the frame dots the green line in blue. */
  dotColor?: string;
  /** Formats the axis ticks and the hover readout. */
  formatValue?: (value: number) => string;
  /**
   * Appended to the hover chip after `formatValue` — the frame's points axis reads `28k pts` while
   * its tier axis reads the tier name alone, so the unit belongs to the caller, not the chart.
   */
  calloutSuffix?: string;
  /**
   * Plot box in pixels. `width` is required for the same reason `TrendChart` requires it: recharts
   * is drawn at an explicit size here and never through ResponsiveContainer, which measures 0 in
   * jsdom.
   */
  width: number;
  height?: number;
  /** Scales the plot to fill its column rather than drawing at `width`, which sets the aspect. */
  fluid?: boolean;
  ariaLabel?: string;
  className?: string;
}

const DEFAULT_HEIGHT = 200;

const STROKE_WIDTH = 2;

const DOT_RADIUS = 3;

/* Axis labels are drawn OUTSIDE the plot, so a sparkline-sized margin would clip them to nothing. */
const PLOT_MARGIN = { top: 8, right: 44, bottom: 8, left: 0 };

const AXIS_TICK = { fill: tokens.color['text-muted'], fontSize: 11 };

const GRID_COLOR = tokens.color['border-subtle'];

/* The rule the last reading sits on, and the chip captioning it. The rule samples #4F46E5 on the
   canvas, which this family already collapses onto --is-accent-purple (`VerticalColumnChart`); the
   chip's own #1B1953 matched no role — the palette carried dark tints only for positive and
   negative — so the PERX3-REDESIGN pass minted --is-tint-accent-bg for it. PLA-8828. */
const REFERENCE_COLOR = tokens.color['accent-purple'];

const BADGE_FILL = tokens.color['tint-accent-bg'];

const BADGE_HEIGHT = 18;

const BADGE_RADIUS = 6;

const BADGE_FONT_SIZE = 11;

const BADGE_PADDING_X = 7;

/* Roboto's average advance at 11px. The chip is sized from the caption rather than measured, because
   an SVG text node has no layout to read before it is painted. */
const BADGE_CHAR_WIDTH = 6.2;

/* Same `fluid` mechanism as TrendChart, and for the same reason it MEASURES rather than stretches:
   a CSS-stretched surface draws correctly but reports the point before or after the one under the
   cursor, because recharts hit-tests against its own internal width. PLA-8828. */
const PlotBox = styled.div<{ $fluid: boolean }>`
  display: ${({ $fluid }) => ($fluid ? 'block' : 'inline-flex')};
  width: ${({ $fluid }) => ($fluid ? '100%' : 'auto')};

  .recharts-cartesian-axis-tick-value {
    font-weight: ${tokens.type['body-weight']};
  }
`;

const finiteReading = (value: number): number | null => (Number.isFinite(value) ? value : null);

const TICK_LINE_HEIGHT = 12;

/* recharts sizes the y gutter from a fixed number, not from the captions it is about to draw, so a
   categorical axis (`Bronze` … `Platinum`) clips against the numeric axis' 38. Derived from the
   longest formatted tick instead, with the numeric gutter as the floor. */
const AXIS_MIN_WIDTH = 38;

const AXIS_GUTTER = 10;

const axisWidthFor = (labels: string[]): number =>
  Math.max(
    AXIS_MIN_WIDTH,
    Math.ceil(Math.max(0, ...labels.map(label => label.length)) * BADGE_CHAR_WIDTH) + AXIS_GUTTER
  );

interface IAxisTickProps {
  x?: number;
  y?: number;
  /** recharts supplies the axis VALUE, which is the row index — see the rows note below. */
  payload?: { value?: string | number };
  labels?: string[];
}

/**
 * The x captions are two-word placeholders on a plot narrower than seven of them laid end to end, so
 * a single-line tick renders them into an illegible run. The frame wraps each caption at its space,
 * which is what this draws — one `tspan` per word, centred under its own tick.
 */
const WrappedAxisTick: React.FC<IAxisTickProps> = ({ x, y, payload, labels }) => {
  const words = String(labels?.[Number(payload?.value)] ?? '')
    .split(/\s+/)
    .filter(Boolean);

  if (words.length === 0) {
    return null;
  }

  return (
    <text
      x={x}
      y={y}
      textAnchor="middle"
      fill={AXIS_TICK.fill}
      fontSize={AXIS_TICK.fontSize}
      data-testid="movement-trend-chart-tick"
    >
      {words.map(word => (
        <tspan key={word} x={x} dy={TICK_LINE_HEIGHT}>
          {word}
        </tspan>
      ))}
    </text>
  );
};

/* The dashed rule under the cursor. recharts owns it, which is the point: driving a rule from React
   state updated in onMouseMove lags the active index by one, so the chip captioned the NEXT point.
   PLA-8828. */
const HOVER_RULE = { stroke: REFERENCE_COLOR, strokeDasharray: '4 4', strokeWidth: 1 };

const Badge = styled.div`
  padding: 0 ${BADGE_PADDING_X}px;
  border-radius: ${BADGE_RADIUS}px;
  background: ${BADGE_FILL};
  color: ${tokens.color['text-primary']};
  font-size: ${BADGE_FONT_SIZE}px;
  line-height: ${BADGE_HEIGHT}px;
  white-space: nowrap;
`;

interface IHoverBadgeProps {
  active?: boolean;
  payload?: { value?: number | string }[];
  format: (value: number) => string;
  suffix?: string;
}

/**
 * The chip the frame shows against the hovered point — `28k pts` on the points axis, `Gold` on the
 * tier axis. It reads the point under the cursor, so it is the SAME chip in both tabs; only the
 * caller's `format` differs.
 */
const HoverBadge: React.FC<IHoverBadgeProps> = ({ active, payload, format, suffix }) => {
  const value = payload?.[0]?.value;

  if (!active || value === undefined || value === null) {
    return null;
  }

  return (
    <Badge data-testid="movement-trend-chart-callout">{`${format(Number(value))}${
      suffix ?? ''
    }`}</Badge>
  );
};

/**
 * PLA-8828: the `POINT & TIER MOVEMENT` trajectory of `3583-10920` — a dotted line on a FIXED,
 * caller-supplied y-axis that opens below zero (`-90k … 90k`). The dashed rule and its chip are drawn
 * against the HOVERED point, not parked on the last one.
 *
 * NOT `TrendChart`, which is the sparkline/area family: its axed variant pins the domain to
 * `[0, 'dataMax']` so a movement series' negative half would be clipped away, and it draws no dot on
 * a resting point. Those are two props it does not have, and it is not this change's to widen.
 */
const MovementTrendChart: React.FC<IMovementTrendChartProps> = ({
  points,
  pointLabels,
  axisTicks,
  // PERX3-REDESIGN round 5: one line and its last-reading dot. The line takes the ramp's brand
  // stop and the dot the darkest one, so the marker still reads against its own line. A caller
  // that passes its own hues keeps them, ramped or not.
  color = seriesStop(2, tokens.color.positive),
  dotColor = seriesStop(1, tokens.color['accent-blue']),
  formatValue = formatNumber,
  calloutSuffix,
  width,
  height = DEFAULT_HEIGHT,
  fluid = false,
  ariaLabel,
  className
}) => {
  // Above the early return: a hook may not be called conditionally.
  const plotRef = React.useRef<HTMLDivElement>(null);
  const measured = useMeasuredWidth(plotRef);

  const readings = (points ?? []).map(finiteReading);

  // One reading, or none, is a level rather than a movement — the panel above draws its own state
  // instead of a sized box with nothing in it.
  if (readings.filter(reading => reading !== null).length < 2) {
    return null;
  }

  /* Keyed by INDEX, not by caption. recharts resolves the hovered point by matching the category
     axis value, and this panel's seven captions are the same placeholder string — so a caption key
     made every point ambiguous and the hover reported a neighbour's reading. PLA-8828. */
  const rows = readings.map((reading, index) => ({
    index,
    reading
  }));

  const domain: [number, number] = [Math.min(...axisTicks), Math.max(...axisTicks)];
  const plotWidth = fluid && measured > 0 ? measured : width;

  return (
    <PlotBox
      ref={plotRef}
      $fluid={fluid}
      className={className}
      role="img"
      aria-label={ariaLabel}
      data-testid="movement-trend-chart"
    >
      <LineChart width={plotWidth} height={height} data={rows} margin={PLOT_MARGIN}>
        <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="index"
          tick={<WrappedAxisTick labels={pointLabels} />}
          tickLine={false}
          axisLine={false}
          interval={0}
          height={30}
        />
        <YAxis
          domain={domain}
          ticks={axisTicks}
          tick={AXIS_TICK}
          tickLine={false}
          axisLine={false}
          width={axisWidthFor(axisTicks.map(formatValue))}
          tickFormatter={formatValue}
        />
        {/* The chip and its rule are HOVER artifacts, not furniture: the frame draws them against
            the point under the cursor, so neither is painted until there is one. */}
        <Tooltip
          cursor={HOVER_RULE}
          content={<HoverBadge format={formatValue} suffix={calloutSuffix} />}
          isAnimationActive={false}
        />
        <Line
          type="linear"
          dataKey="reading"
          stroke={color}
          strokeWidth={STROKE_WIDTH}
          dot={{ r: DOT_RADIUS, stroke: 'none', fill: dotColor }}
          activeDot={{ r: DOT_RADIUS + 1, stroke: 'none', fill: dotColor }}
          connectNulls={false}
          isAnimationActive={false}
        />
      </LineChart>
    </PlotBox>
  );
};

export default MovementTrendChart;
